/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisStepper } from 'src/core/components/luisStepper/LuisStepper';
import { LuisStepperStepType } from 'src/core/components/luisStepper/LuisStepperStep';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { AutoLabelingInfoDto, AutoLabelingSteps } from 'src/customText/verticals/autotagTriggerModal/types';
import { getAutoLabelingStepStatus } from 'src/customText/verticals/autotagTriggerModal/utils';

export type AutoLabelingModalStepsNavigatorProps = {
    activeStep: number;
    horizontal?: boolean;
    autoLabelingInfoDto: AutoLabelingInfoDto;
    onActiveStepChange: (newStep: number) => void;
};

const messages = defineMessages({
    selectFiles: { id: 'customText.autoLabelingModalStepsNavigator.selectFiles', defaultMessage: 'Select files' },
    configureJob: { id: 'customText.autoLabelingModalStepsNavigator.configureJob', defaultMessage: 'Configure job' },
    selectClasses: { id: 'customText.autoLabelingModalStepsNavigator.selectClasses', defaultMessage: 'Select classes' },
    selectJobType: { id: 'customText.autoLabelingModalStepsNavigator.selectJobType', defaultMessage: 'Select job type' },
    selectEntities: { id: 'customText.autoLabelingModalStepsNavigator.selectEntities', defaultMessage: 'Select entities' },
    reviewAndFinish: { id: 'customText.autoLabelingModalStepsNavigator.reviewAndFinish', defaultMessage: 'Review and finish' },
    autoLabelingType: { id: 'customText.autoLabelingModalStepsNavigator.autoLabelingType', defaultMessage: 'Pick auto-labeling type' }
});

export const AutoLabelingModalStepsNavigator = observer((props: AutoLabelingModalStepsNavigatorProps) => {
    const { horizontal, activeStep, autoLabelingInfoDto, onActiveStepChange } = props;
    const { localize } = useLocalization();
    const { rootStore } = useGlobalState();

    const { projectKind } = rootStore.currentCustomTextProjectStore;

    const steps: LuisStepperStepType[] = [
        {
            id: 'autoLabelingType',
            title: localize(messages.autoLabelingType),
            state: getAutoLabelingStepStatus(AutoLabelingSteps.AutoLabelingJob, activeStep, autoLabelingInfoDto, rootStore).state,
            disabled: !getAutoLabelingStepStatus(AutoLabelingSteps.AutoLabelingJob, activeStep, autoLabelingInfoDto, rootStore).isValid,
            children: [
                {
                    id: 'selectJobType',
                    title: localize(messages.selectJobType),
                    state: getAutoLabelingStepStatus(AutoLabelingSteps.AutoLabelingJob, activeStep, autoLabelingInfoDto, rootStore).state,
                    disabled: !getAutoLabelingStepStatus(AutoLabelingSteps.AutoLabelingJob, activeStep, autoLabelingInfoDto, rootStore)
                        .isValid
                },
                {
                    id: 'configureJob',
                    title: localize(messages.configureJob),
                    state: getAutoLabelingStepStatus(AutoLabelingSteps.ConfigureJob, activeStep, autoLabelingInfoDto, rootStore).state,
                    disabled: !getAutoLabelingStepStatus(AutoLabelingSteps.ConfigureJob, activeStep, autoLabelingInfoDto, rootStore).isValid
                }
            ]
        },
        {
            id: 'selectEntitiesOrClasses',
            title: localize(projectKind === 'CustomEntityRecognition' ? messages.selectEntities : messages.selectClasses),
            state: getAutoLabelingStepStatus(AutoLabelingSteps.SelectEntitiesOrClasses, activeStep, autoLabelingInfoDto, rootStore).state,
            disabled: !getAutoLabelingStepStatus(AutoLabelingSteps.SelectEntitiesOrClasses, activeStep, autoLabelingInfoDto, rootStore)
                .isValid
        },
        {
            id: 'selectFiles',
            title: localize(messages.selectFiles),
            state: getAutoLabelingStepStatus(AutoLabelingSteps.SelectFiles, activeStep, autoLabelingInfoDto, rootStore).state,
            disabled: !getAutoLabelingStepStatus(AutoLabelingSteps.SelectFiles, activeStep, autoLabelingInfoDto, rootStore).isValid
        },
        {
            id: 'reviewAndFinish',
            title: localize(messages.reviewAndFinish),
            state: getAutoLabelingStepStatus(AutoLabelingSteps.ReviewAndFinish, activeStep, autoLabelingInfoDto, rootStore).state,
            disabled: !getAutoLabelingStepStatus(AutoLabelingSteps.ReviewAndFinish, activeStep, autoLabelingInfoDto, rootStore).isValid
        }
    ];

    const onStepClick = (stepKey: string) => {
        if (stepKey === 'configureJob') {
            onActiveStepChange(AutoLabelingSteps.ConfigureJob);
        } else {
            onActiveStepChange(steps.findIndex(step => step.id === stepKey));
        }
    };

    return <LuisStepper horizontal={horizontal} onStepClick={onStepClick} steps={steps} />;
});
